import { Checkbox, ListItemText, MenuItem, TextField } from "@mui/material";
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import { DatePicker } from "@mui/x-date-pickers";
import { useTranslation } from "react-i18next";
import {useRecoilValue, useSetRecoilState} from "recoil";
import * as Yup from "yup";
import AdvancedFilter from "../../../component/AdvancedFilter";
import {addFilter, getFilterValue, renderMultiSelect} from "../../../utils/FilterUtils";
import {
    ID_FILTER_CONTRACTOR, ID_FILTER_DEALERS, ID_FILTER_DMS,
    ID_FILTER_LEGAL_ENTITIES,
    ID_FILTER_NAME,
    ID_FILTER_PRINTOUT_CREATIONDATEFROM,
    ID_FILTER_PRINTOUT_CREATIONDATETO,
    ID_FILTER_PRINTOUT_SENTDATEFROM,
    ID_FILTER_PRINTOUT_SENTDATETO,
    ID_FILTER_SEARCH,
    ID_FILTER_STATUSES
} from "../constants/RoyaltiesFilters";
import { STATE_PRINTOUT_ACTIVE_FILTERS } from "../constants/RoyaltiesRecoil";
import { ID_AIRPORT, ID_CREDITAGRICOLE, ID_STATION } from "../constants/RoyaltiesContractors";
import { ID_ERROR, ID_PROCESSING, ID_PUBLISHED, ID_TO_SEND } from "../constants/RoyaltiesStatuses";
import { COLOR_ERROR, COLOR_PUBLISHED, COLOR_TO_SEND } from "../constants/RoyaltiesColors";
import AsyncAutocomplete from "../../../component/AsyncAutocomplete";
import { useRoyaltiesPrintoutRepository } from "../../../api/repositories/RoyaltiesPrintoutRepository";
import ColouredDot from "../../../component/ColouredDot";
import {RoyaltyContractor, RoyaltyDMS} from "../constants/RoyaltiesModels";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";

function ReportsFilter() {

    const { t } = useTranslation('finance', { keyPrefix: 'royalties' });
    const { t: tg } = useTranslation("translation");
    const { getFindTypesContractor, getContractorsList, retrieveDms } = useRoyaltiesPrintoutRepository();
    const filterDefs = useRecoilValue(STATE_PRINTOUT_ACTIVE_FILTERS);

    const setFilters = useSetRecoilState(STATE_PRINTOUT_ACTIVE_FILTERS);
    const [search, setSearch] = useState("");
    const [dmsNameList, setDmsNameList] = useState<RoyaltyDMS[]>([]);
    const [contractorsList, setContractorsList] = useState<RoyaltyContractor[]>([]);
    const {
        globalData: { user },
    } = useContext(GlobalContext);

    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
        value: x.name,
        label: tg(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
        value: x.name,
        label: tg(`dealers.${x.name}`),
        }));

    const [dealers, setDealers] = useState<any[]>(dealerOptions);
    const schema = Yup.object().shape({
        name: Yup.string().nullable(),
        dms: Yup.string().nullable(),
        contractors: Yup.string().nullable(),
        statuses: Yup.array(),
        creationDateFrom: Yup.date().nullable().test("testFrom", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.creationDateTo || (value <= context.parent.creationDateTo);
        }).typeError(t('filters.errors.invalid-date')),
        creationDateTo: Yup.date().nullable().test("testTo", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.creationDateFrom || (value >= context.parent.creationDateFrom);
        }).typeError(t('filters.errors.invalid-date')),
        sentDateFrom: Yup.date().nullable().test("testFrom", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.sentDateTo || (value <= context.parent.sentDateTo);
        }).typeError(t('filters.errors.invalid-date')),
        sentDateTo: Yup.date().nullable().test("testTo", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.sentDateFrom || (value >= context.parent.sentDateFrom);
        }).typeError(t('filters.errors.invalid-date')),
        dealers: Yup.array(),
        legalEntities: Yup.array().min(1, tg('validation.selectAtLeastOne'))
    });

    const defaultValues = {
        name: getFilterValue(ID_FILTER_NAME, filterDefs) || "",
        dms: getFilterValue(ID_FILTER_DMS, filterDefs) || "",
        contractors: getFilterValue(ID_FILTER_CONTRACTOR, filterDefs) || "",
        statuses:  getFilterValue(ID_FILTER_STATUSES, filterDefs) || [],
        creationDateFrom: getFilterValue(ID_FILTER_PRINTOUT_CREATIONDATEFROM, filterDefs) || null,
        creationDateTo: getFilterValue(ID_FILTER_PRINTOUT_CREATIONDATETO, filterDefs) || null,
        sentDateFrom: getFilterValue(ID_FILTER_PRINTOUT_SENTDATEFROM, filterDefs) || null,
        sentDateTo: getFilterValue(ID_FILTER_PRINTOUT_SENTDATETO, filterDefs) || null,
        legalEntities:
            getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) ||
            legalEntityOptions.map((x: any) => x.value),
        dealers: getFilterValue(ID_FILTER_DEALERS, filterDefs) || [],
    };

    const printoutDefaultValues = {
        name: "",
        dms: "",
        contractors: "",
        statuses: [],
        creationDateFrom: null,
        creationDateTo: null,
        sentDateFrom: null,
        sentDateTo: null
    };

    useEffect( () => {
        getContractorsList().then((res: any) => {
            setContractorsList(res);
        });
    },[])
    function getDMSList(contractorId : string){
        retrieveDms(contractorId).then((res: any) => {
            if(res){
                setDmsNameList(res);
            } else {
                setDmsNameList([]);
            }

        });
    }
    const renderContractorNames = (field: any, error: any, state: any, values: any, setValue : any) => (

        <TextField fullWidth
            select
            id={"name"}
            label={t('filters.printouts.' + ID_FILTER_NAME)}
            value={field.value}
            onChange={(e) => {
                if (e.target.value === "") {
                    setDmsNameList([]);
                    setValue("dms", "");
                }
                field.onChange(e.target.value);
            }}
        >
            <MenuItem key={""} value={""}/>
            {contractorsList.map((option: any) => (
                <MenuItem key={option.id} value={option.name} onClick={(e) => getDMSList(option.id)}>
                    {option.name}
                </MenuItem>
            ))}
        </TextField>

    );

    const renderDMS = (field: any) => (

        <TextField
            fullWidth
            id={"dms"}
            label={t('filters.printouts.' + ID_FILTER_DMS)}
            select
            disabled={dmsNameList.length === 0}
            value={field.value}
            onChange={field.onChange}
        >
            {dmsNameList?.map((dms) => (
                <MenuItem key={dms.dmsName} value={dms.dmsName}>
                    {dms.dmsName}
                </MenuItem>
            ))}
        </TextField>

    );

    const renderContractors = (field: any) => (
        <AsyncAutocomplete
            id={"contractors"}
            callback={getFindTypesContractor}
            initialValues={true}
            value={(field.value && field.value.length !== 0) ? field.value : null}
            onChange={field.onChange}
            renderInput={(params) => <TextField
                {...params}
                label={t('filters.printouts.' + ID_FILTER_CONTRACTOR)}
            />}
        />
    );

    const renderStatuses = (field: any) => (
            <TextField fullWidth
                       label={t('filters.printouts.' + ID_FILTER_STATUSES)}
                       select
                       SelectProps={{
                           multiple: true,
                           renderValue: (selected: any) => selected.map((s: any) => statuses.find(ss => ss.value === s)!.label).join(', ')
                       }}
                       value={field.value}
                       onChange={field.onChange}
            >
                {statuses.map((status) =>
                        <MenuItem key={status.value} value={status.value}>
                            <Checkbox checked={field.value.indexOf(status.value) > -1}/>
                            <ColouredDot color={status?.color}/>
                            <ListItemText primary={status.label}/>
                        </MenuItem>
                )}
            </TextField>
    );

    const renderCreationDateFrom = (field: any, error: any, state: any, values: any) => (
            <DatePicker label={t('filters.printouts.' + ID_FILTER_PRINTOUT_CREATIONDATEFROM)}
                        views={['day']}
                        maxDate={values['creationDateTo'] !== null ? values['creationDateTo'] : null}
                        value={field.value}
                        onChange={field.onChange}
                        renderInput={(params) => <TextField {...params} fullWidth
                                                            id={"creationDateFrom"}
                                                            error={error !== undefined}
                                                            helperText={error ? error.message : ""}
                        />}
                        inputFormat={"L"}
            />
    );

    const renderCreationDateTo = (field: any, error: any, state: any, values: any) => (
            <DatePicker label={t('filters.printouts.' + ID_FILTER_PRINTOUT_CREATIONDATETO)}
                        views={['day']}
                        minDate={values['creationDateFrom'] !== null ? values['creationDateFrom'] : null}
                        value={field.value}
                        onChange={field.onChange}
                        renderInput={(params) => <TextField {...params} fullWidth
                                                            id={"creationDateTo"}
                                                            error={error !== undefined}
                                                            helperText={error ? error.message : ""}
                        />}
                        inputFormat={"L"}
            />
    );

    const renderSentDateFrom = (field: any, error: any, state: any, values: any) => (
            <DatePicker label={t('filters.printouts.' + ID_FILTER_PRINTOUT_SENTDATEFROM)}
                        views={['day']}
                        maxDate={values['sentDateTo'] !== null ? values['sentDateTo'] : null}
                        value={field.value}
                        onChange={field.onChange}
                        renderInput={(params) => <TextField {...params} fullWidth
                                                            id={"sentDateFrom"}
                                                            error={error !== undefined}
                                                            helperText={error ? error.message : ""}
                        />}
                        inputFormat={"L"}
            />
    );

    const renderSentDateTo = (field: any, error: any, state: any, values: any) => (
            <DatePicker label={t('filters.printouts.' + ID_FILTER_PRINTOUT_SENTDATETO)}
                        views={['day']}
                        minDate={values['sentDateFrom'] !== null ? values['sentDateFrom'] : null}
                        value={field.value}
                        onChange={field.onChange}
                        renderInput={(params) => <TextField {...params} fullWidth
                                                            id={"sentDateTo"}
                                                            error={error !== undefined}
                                                            helperText={error ? error.message : ""}
                        />}
                        inputFormat={"L"}
            />
    );

    const fields = {
        name: renderContractorNames,
        dms: renderDMS,
        contractors: renderContractors,
        statuses: renderStatuses,
        creationDateFrom: renderCreationDateFrom,
        creationDateTo: renderCreationDateTo,
        sentDateFrom: renderSentDateFrom,
        sentDateTo: renderSentDateTo,
        legalEntities: renderMultiSelect(
            tg("shared.legalEntities"),
            legalEntityOptions,
            "legalEntities",
            legalEntityOptions.length <= 1,
            (value: any, setValue: any) => {
            setValue('dealers', []);
            let dealers = user?.dealers
                ?.filter(
                (x: any) =>
                    value?.length > 0 &&
                    x.legalEntities.some((e: any) => value.includes(e.name)) &&
                    x.name.toLowerCase() !== "all"
                )
                .map((x: any) => {
                return {
                    label: tg(`dealers.${x.name}`),
                    value: x.name,
                };
                });
            setDealers(dealers);
            }
        ),
        dealers: renderMultiSelect(tg("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1)
    }

    const contractors = [
        {
            value: ID_AIRPORT,
            label: t('contractors.' + ID_AIRPORT)
        },
        {
            value: ID_STATION,
            label: t('contractors.' + ID_STATION)
        },
        {
            value: ID_CREDITAGRICOLE,
            label: t('contractors.' + ID_CREDITAGRICOLE)
        },
    ];

    const statuses = [
        {
            value: ID_PUBLISHED,
            label: t('statuses.' + ID_PUBLISHED),
            color: COLOR_PUBLISHED + '.transparent'
        },
        {
            value: ID_TO_SEND,
            label: t('statuses.' + ID_TO_SEND),
            color: COLOR_TO_SEND + '.transparent'
        },
        {
            value: ID_PROCESSING,
            label: t('statuses.' + ID_PROCESSING),
        },
        {
            value: ID_ERROR,
            label: t('statuses.' + ID_ERROR),
            color: COLOR_ERROR + '.transparent'
        }
    ];

    const submit = (data: any) => {
        addFilter(setFilters, ID_FILTER_SEARCH, search);
        addFilter(setFilters, ID_FILTER_NAME, data.name);
        addFilter(setFilters, ID_FILTER_PRINTOUT_CREATIONDATEFROM, data.creationDateFrom);
        addFilter(setFilters, ID_FILTER_PRINTOUT_CREATIONDATETO, data.creationDateTo);
        addFilter(setFilters, ID_FILTER_PRINTOUT_SENTDATEFROM, data.sentDateFrom);
        addFilter(setFilters, ID_FILTER_PRINTOUT_SENTDATETO, data.sentDateTo);
        addFilter(setFilters, ID_FILTER_CONTRACTOR, data.contractors);
        addFilter(setFilters, ID_FILTER_DMS, data.dms);
        addFilter(setFilters, ID_FILTER_STATUSES, data.statuses);
        addFilter(setFilters, ID_FILTER_LEGAL_ENTITIES, data.legalEntities);
        addFilter(setFilters, ID_FILTER_DEALERS, data.dealers);
    }

    const reset = () => {
        setFilters([]);
    }

    return (
            <AdvancedFilter schema={schema} fields={fields} defaultValues={defaultValues}
                            searchInput={search} setSearchInput={setSearch}
                            onSubmit={submit}
                            onReset={reset}
                            customDefaultValues={printoutDefaultValues}
            />
    )
}

export default ReportsFilter;